/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useMutation, useQueryClient} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {createJob} from '@ion/core/src/services/Connect';
import {CreateJob} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export type UseRerunJobResult = UseDefaultQueryResult & {
  rerunJob: (job: CreateJob, callbacks) => void;
}

const api = async(job: CreateJob) => {
  try {
    return await createJob({job});
  } catch (err) {
    error(err);
    throw new Error(`An ${err.status || ''} Error Occurred`);
  }
};

/**
 * Common hook for rerunning a Job entity.
 *
 * ```typescript
 *
 *    import useRerunJob from '@ion/query/src/hooks/';
 *
 *    const {resendShipment, isSuccess, isError} = useRerunJob();
 *
 * ```
 */
export const useRerunJob = (): UseRerunJobResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSettled : () => {
      queryClient.invalidateQueries([QueryKey.RERUN_JOB]);
    }
  });

  return createMutationResult(
    mutation, {
      rerunJob : mutation.mutate
    }
  ) as UseRerunJobResult;
};

export default useRerunJob;
